/**
 * messaging.ts — FCM 웹 푸시(잠금화면 출하 알림) 수신 서비스.
 *
 * 모든 firebase/* import는 services/ 안에서만 한다(규칙). UI는 이 모듈의
 * 함수만 호출한다.
 *
 * Firestore 경로 계약 (백엔드가 그대로 읽음 — 키 이름 변경 금지):
 *   users/{uid}/preferences/push
 *     { enabled: boolean,                         // 백엔드는 true 인 문서만 본다 — 기기 하나를 끌 때 false 로 바꾸지 않는다
 *       tokens: Record<token-string, { device_id, at }>,  // 12 설계 §4-2: 값만 객체로 확장. 옛 ISO 문자열도 읽는다.
 *                                                  // 백엔드는 Object.keys(tokens) 만 쓴다(값 형식 무관)
 *       types: Record<PushNotifyType, boolean>,   // 그룹 종류(가입 요청 등) 수신 on/off (누락/미설정 = ON)
 *       scopes: { [`{법인}_{사업}`]: Record<PushNotifyType, boolean> },  // ★ 사업부 종류(출하 등) 사업부별 on/off
 *       updated_at: serverTimestamp }
 *   users/{uid}/devices/{deviceId}.push_token     // 이 기기 토큰 — tokens 와 양방향 일치(§4-2)
 *
 * 이 기기 푸시 켜기/끄기는 **기기 단위**(12 설계 §8-D D-2): 끄기 = 이 기기 토큰만 지움, 다른 기기 토큰은 유지.
 *
 * iOS 주의: iOS는 홈화면에 추가한 standalone PWA + 16.4+ 에서만 웹 푸시가 동작한다.
 * 미설치/미지원이면 isPushSupported()가 false → UI에서 안내한다.
 */
import { deleteToken, getMessaging, getToken, onMessage, isSupported, type Messaging } from "firebase/messaging";
import { deleteField, doc, FieldPath, onSnapshot, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { app, db } from "./firebase";
import { PUSH_NOTIFY_TYPES, type PushNotifyType } from "@/domain/push_copy";
import { getMyDevice, setMyDevicePushToken } from "./devices.repository";
import { getDeviceId, isSharedSession } from "@/lib/sessionLocal";

// 종류 정의의 단일 소스는 domain/push_copy. 여기선 푸시 토큰/수신설정 로직에서 쓰기 위해 재export.
export { PUSH_NOTIFY_TYPES };
export type { PushNotifyType };

/** 웹 푸시 인증서 VAPID 공개키 (공개키라 코드에 박아도 안전). */
const VAPID_KEY = "BDys_lD4N5k6iLYQNK5obBMxLSc7V0CEVtj-WYhS31NYT26IUrZWigv7-xc34vw_ifSShtcue7zRFAaWGPyvQwk";

export type PushTypePrefs = Record<PushNotifyType, boolean>;

/** 저장값(부분/누락)을 전체 종류 맵으로 정규화 — 누락/미설정 키는 ON 기본값. */
export function resolvePushTypes(raw: unknown): PushTypePrefs {
  const src = (raw ?? {}) as Record<string, unknown>;
  const out = {} as PushTypePrefs;
  for (const k of PUSH_NOTIFY_TYPES) out[k] = src[k] !== false; // 미설정/누락 → true
  return out;
}

/** 메시징 인스턴스 캐시 (지원 환경에서만 생성). */
let _messaging: Messaging | null = null;
let _messagingTried = false;

async function getMessagingInstance(): Promise<Messaging | null> {
  if (_messagingTried) return _messaging;
  _messagingTried = true;
  try {
    if (await isSupported()) {
      _messaging = getMessaging(app);
    }
  } catch {
    _messaging = null;
  }
  return _messaging;
}

/**
 * 이 환경에서 웹 푸시를 쓸 수 있는지.
 * - FCM 지원(서비스워커/Push API) + Notification API 존재
 * - iOS는 standalone PWA(홈화면 추가)일 때만 Notification/Push가 노출됨 → 자동 false
 */
export async function isPushSupported(): Promise<boolean> {
  try {
    if (typeof window === "undefined") return false;
    if (!("Notification" in window)) return false;
    if (!("serviceWorker" in navigator)) return false;
    return await isSupported();
  } catch {
    return false;
  }
}

/** iOS Safari에서 홈화면 추가(standalone) 상태가 아니면 true → 안내용. */
export function isIosNeedsStandalone(): boolean {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document);
  if (!isIos) return false;
  // standalone PWA 여부 — iOS Safari는 navigator.standalone, 표준은 display-mode 미디어쿼리.
  const standalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches === true;
  return !standalone;
}

/**
 * 브라우저 알림 권한 현재값.
 * - "default"  → 아직 안 물어봄 → 버튼 누르면 시스템 팝업이 뜸
 * - "granted"  → 이미 허용 → 팝업 없이 토큰 재등록만
 * - "denied"   → 이미 거부 → requestPermission()을 다시 불러도 팝업 안 뜸(즉시 denied).
 *               되돌리려면 사용자가 기기 설정에서 직접 허용해야 함.
 * - "unsupported" → Notification API 자체가 없음(iOS 미설치 등)
 */
export type PushPermission = "default" | "granted" | "denied" | "unsupported";

export interface PushEnvState {
  supported: boolean; // isPushSupported (FCM/SW/Push API)
  needsStandalone: boolean; // iOS인데 홈화면 미설치
  permission: PushPermission;
}

/**
 * 버튼을 누르기 "전에" UI가 상태별 분기를 하기 위한 종합 조회.
 * Notification.permission은 동기 조회라 팝업을 띄우지 않는다(안전).
 */
export async function getPushState(): Promise<PushEnvState> {
  const supported = await isPushSupported();
  const needsStandalone = isIosNeedsStandalone();
  let permission: PushPermission = "unsupported";
  if (typeof window !== "undefined" && "Notification" in window) {
    permission = Notification.permission as PushPermission; // default | granted | denied
  }
  return { supported, needsStandalone, permission };
}

/**
 * 등록된 SW 가 "activated" 될 때까지 기다린다.
 * getToken 에 serviceWorkerRegistration 을 직접 넘기면 SDK 는 활성화를 기다려 주지 않는다(기본 경로에서만 기다림).
 * 처음 켜는 기기(2026-11-04 갤럭시 Chrome)는 막 등록한 SW 가 설치 중이라 pushManager.subscribe 가
 * "Subscription failed - no active Service Worker" 로 실패했다.
 */
function waitActive(reg: ServiceWorkerRegistration, timeoutMs = 15000): Promise<void> {
  if (reg.active) return Promise.resolve();
  const sw = reg.installing || reg.waiting;
  if (!sw) return Promise.reject(new Error("서비스워커를 찾지 못했습니다."));
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      sw.removeEventListener("statechange", onChange);
      reject(new Error("서비스워커 준비 시간이 지났습니다."));
    }, timeoutMs);
    function onChange() {
      if (sw!.state === "activated") {
        clearTimeout(timer);
        sw!.removeEventListener("statechange", onChange);
        resolve();
      } else if (sw!.state === "redundant") {
        clearTimeout(timer);
        sw!.removeEventListener("statechange", onChange);
        reject(new Error("서비스워커 설치에 실패했습니다."));
      }
    }
    sw.addEventListener("statechange", onChange);
  });
}

/** FCM SW를 등록(없으면)하고, 활성화될 때까지 기다린 뒤 등록 객체를 반환. */
async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  if (!("serviceWorker" in navigator)) return undefined;
  try {
    const reg =
      (await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js")) ??
      (await navigator.serviceWorker.register("/firebase-messaging-sw.js"));
    await waitActive(reg);
    return reg;
  } catch (e) {
    console.warn("[messaging] 서비스워커 준비 실패 — SDK 기본 등록으로 넘김", e);
    return undefined; // getToken 이 기본 경로로 직접 등록·대기한다
  }
}

export interface EnablePushResult {
  ok: boolean;
  reason?: string;
}

/**
 * 푸시 활성화. **반드시 사용자 제스처(버튼/토글) 안에서 호출**할 것
 * (Notification.requestPermission이 제스처를 요구).
 *
 * granted → 토큰 발급 → users/{uid}/preferences/push 에 merge 저장.
 */
/** 이 탭이 쓰는 기기 ID — 공용 PC 로그인(기기 미등록)이면 null */
function currentPushDeviceId(): string | null {
  return isSharedSession() ? null : getDeviceId();
}

export async function enablePush(uid: string): Promise<EnablePushResult> {
  const deviceId = currentPushDeviceId();
  if (!(await isPushSupported())) {
    return { ok: false, reason: "이 브라우저/기기에서는 웹 푸시를 지원하지 않습니다." };
  }

  const messaging = await getMessagingInstance();
  if (!messaging) {
    return { ok: false, reason: "메시징을 초기화할 수 없습니다." };
  }

  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch {
    return { ok: false, reason: "알림 권한 요청에 실패했습니다." };
  }
  if (permission !== "granted") {
    return {
      ok: false,
      reason:
        permission === "denied"
          ? "브라우저 알림 권한이 거부되었습니다. 브라우저 설정에서 알림을 허용해 주세요."
          : "알림 권한이 허용되지 않았습니다.",
    };
  }

  const registration = await ensureServiceWorker();

  let token: string;
  try {
    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      ...(registration ? { serviceWorkerRegistration: registration } : {}),
    });
  } catch (e) {
    return { ok: false, reason: `푸시 토큰 발급 실패: ${(e as Error).message}` };
  }
  if (!token) {
    return { ok: false, reason: "푸시 토큰을 발급받지 못했습니다." };
  }

  try {
    await setDoc(
      pushDocRef(uid),
      {
        enabled: true,
        // tokens 는 merge 시 키 단위로 합쳐짐 → 기존 토큰 보존, 이 기기 토큰 갱신.
        tokens: { [token]: { device_id: deviceId, at: new Date().toISOString() } },
        updated_at: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (e) {
    return { ok: false, reason: `설정 저장 실패: ${(e as Error).message}` };
  }

  // 기기 문서에도 토큰 기록(D-2 표시·관리자 기기 해제 때 함수가 이 토큰을 지움). 기기 문서가 없으면(규칙 미배포 등) 건너뜀
  if (deviceId) {
    try {
      await setMyDevicePushToken(uid, deviceId, token);
    } catch (e) {
      console.warn("[messaging] 기기 문서 push_token 기록 실패(무시)", e);
    }
  }

  return { ok: true };
}

/** 이 기기에서 쓰던 토큰 찾기 — 기기 문서 push_token, 없으면 알림 허용 상태에서 FCM 에 다시 묻기(팝업 없음) */
async function findThisDeviceToken(uid: string, deviceId: string | null): Promise<string | null> {
  if (deviceId) {
    try {
      const d = await getMyDevice(uid, deviceId);
      if (d?.push_token) return d.push_token;
    } catch {
      /* 규칙 미배포 등 — 아래로 */
    }
  }
  if (typeof window === "undefined" || !("Notification" in window) || Notification.permission !== "granted") return null;
  const messaging = await getMessagingInstance();
  if (!messaging) return null;
  try {
    const registration = await ensureServiceWorker();
    return (
      (await getToken(messaging, {
        vapidKey: VAPID_KEY,
        ...(registration ? { serviceWorkerRegistration: registration } : {}),
      })) || null
    );
  } catch {
    return null;
  }
}

/**
 * 이 기기 푸시 끄기 — 이 기기 토큰만 지운다(다른 기기 토큰·enabled 는 그대로).
 * 예전 동작(enabled:false 로 전체 끄기)은 기기 단위 설계(§8-D D-2)로 바뀌었다.
 */
export async function disablePush(uid: string): Promise<void> {
  const deviceId = currentPushDeviceId();
  const token = await findThisDeviceToken(uid, deviceId);
  if (token) {
    await updateDoc(pushDocRef(uid), new FieldPath("tokens", token), deleteField(), "updated_at", serverTimestamp());
    const messaging = await getMessagingInstance();
    if (messaging) {
      try {
        await deleteToken(messaging);
      } catch {
        /* 토큰 무효화 실패는 무시 — Firestore 에서 지웠으므로 더는 발송되지 않는다 */
      }
    }
  }
  if (deviceId) {
    try {
      await setMyDevicePushToken(uid, deviceId, null);
    } catch {
      /* 기기 문서 없음 — 무시 */
    }
  }
}

/** 알림 종류 하나의 수신 on/off 저장 (merge — 다른 종류/토큰 보존). */
export async function setPushType(uid: string, type: PushNotifyType, on: boolean): Promise<void> {
  await setDoc(
    pushDocRef(uid),
    { types: { [type]: on }, updated_at: serverTimestamp() },
    { merge: true },
  );
}

/** 사업부별 업무 알림 on/off 저장 (merge). scopeKey = pushScopeKey(법인, 사업) — 예 "joongang_rmc" */
export async function setPushScopeType(
  uid: string,
  scopeKey: string,
  type: PushNotifyType,
  on: boolean,
): Promise<void> {
  await setDoc(
    pushDocRef(uid),
    { scopes: { [scopeKey]: { [type]: on } }, updated_at: serverTimestamp() },
    { merge: true },
  );
}

/** 토큰 한 개의 값 — 옛 형식(ISO 문자열)은 device_id 없이 at 만 */
export interface PushTokenEntry {
  device_id: string | null;
  at: string | null;
}

function parseTokens(raw: unknown): Record<string, PushTokenEntry> {
  const out: Record<string, PushTokenEntry> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "string") out[k] = { device_id: null, at: v };
    else if (v && typeof v === "object") {
      const o = v as { device_id?: unknown; at?: unknown };
      out[k] = {
        device_id: typeof o.device_id === "string" ? o.device_id : null,
        at: typeof o.at === "string" ? o.at : null,
      };
    } else out[k] = { device_id: null, at: null };
  }
  return out;
}

/** 사업부별 값은 저장된 것만 남긴다(미설정 = undefined) — 화면이 옛 types 값으로 대신 보이게 */
function parseScopes(raw: unknown): Record<string, Partial<PushTypePrefs>> {
  const out: Record<string, Partial<PushTypePrefs>> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const src = (v ?? {}) as Record<string, unknown>;
    const one: Partial<PushTypePrefs> = {};
    for (const t of PUSH_NOTIFY_TYPES) if (typeof src[t] === "boolean") one[t] = src[t] as boolean;
    out[k] = one;
  }
  return out;
}

/**
 * 사업부 종류 하나가 지금 켜져 있는지(개인 설정). scopes[사업부][종류] → 없으면 옛 types[종류] → 없으면 켜짐.
 * 백엔드(A 묶음 collectRecipientTokens)도 같은 순서로 보도록 맞춘다.
 */
export function effectiveScopeOn(pref: PushPref, scopeKey: string, type: PushNotifyType): boolean {
  const v = pref.scopes[scopeKey]?.[type];
  return typeof v === "boolean" ? v : pref.types[type];
}

export interface PushPref {
  enabled: boolean;
  tokens: Record<string, PushTokenEntry>;
  /** 그룹 종류(가입 요청 등) */
  types: PushTypePrefs;
  /** 사업부 종류 — 키 = pushScopeKey. 저장된 값만(미설정은 effectiveScopeOn 이 대신 판정) */
  scopes: Record<string, Partial<PushTypePrefs>>;
}

/** 이 기기가 지금 푸시를 받는지 — 토큰 목록에 이 기기 토큰(또는 이 기기 ID 로 적힌 토큰)이 있음 */
export function isDevicePushOn(pref: PushPref, deviceId: string | null, devicePushToken: string | null): boolean {
  if (!pref.enabled) return false;
  if (devicePushToken && pref.tokens[devicePushToken]) return true;
  if (!deviceId) return false;
  return Object.values(pref.tokens).some((t) => t.device_id === deviceId);
}

/** 현재 사용자의 push 설정 구독. */
export function watchPushPref(
  uid: string,
  cb: (pref: PushPref) => void,
  onError?: (e: Error) => void,
): () => void {
  return onSnapshot(
    pushDocRef(uid),
    (snap) => {
      const data = snap.exists() ? snap.data() : {};
      cb({
        enabled: data.enabled === true,
        tokens: parseTokens(data.tokens),
        types: resolvePushTypes(data.types),
        scopes: parseScopes(data.scopes),
      });
    },
    onError,
  );
}

/**
 * 포그라운드 onMessage 구독 — 앱이 열려 있을 때 받은 메시지를 콜백에 넘긴다.
 * (잠금화면 알림은 SW가 처리하므로 여기선 토스트 등 인앱 표시용.)
 * 지원 안 되면 no-op unsubscribe 반환.
 */
export function onForegroundMessage(
  cb: (msg: { title?: string; body?: string; link?: string }) => void,
): () => void {
  let unsub: (() => void) | null = null;
  let cancelled = false;
  void (async () => {
    const messaging = await getMessagingInstance();
    if (!messaging || cancelled) return;
    unsub = onMessage(messaging, (payload) => {
      const n = payload.notification;
      const d = payload.data ?? {};
      cb({
        title: n?.title ?? d.title,
        body: n?.body ?? d.body,
        link: (payload.fcmOptions as { link?: string } | undefined)?.link ?? d.link,
      });
    });
  })();
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}

function pushDocRef(uid: string) {
  return doc(db, "users", uid, "preferences", "push");
}
